import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input, Card, Alert, Checkbox } from '../components/ui';

/**
 * Login page component
 * @returns {React.ReactElement} Login page component
 */
const LoginPage = () => {
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  // Authentication context and navigation
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // If user is already logged in, redirect to home page
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  /**
   * Handle form submission
   * @param {React.FormEvent} e - Form event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('请输入用户名和密码');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const result = await login(username, password);
      
      if (result.success) {
        // If login is successful, navigate to the dashboard
        navigate('/');
      } else {
        setError(result.message || '登录失败，请检查用户名和密码');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('登录过程中发生错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // User icon for input
  const UserIcon = (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  // Lock icon for password input
  const LockIcon = (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50/30 to-indigo-50/20">
      <div className="min-h-screen flex">
        {/* Left Side - System Introduction */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-emerald-600"></div>
          {/* Floating Elements */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
          <div className="absolute bottom-32 right-16 w-24 h-24 bg-white/10 rounded-full blur-lg"></div>
          <div className="absolute top-1/2 right-32 w-16 h-16 bg-white/5 rounded-full blur-md"></div>
          
          <div className="relative z-10 flex flex-col justify-center items-center px-8 lg:px-12 xl:px-20 text-white">
            <div className="max-w-xl lg:max-w-lg xl:max-w-xl lg:ml-8 xl:ml-12">
            {/* Logo and Brand */}
            <div className="mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mr-4">
                  <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl font-bold">国保中医调理中心</h1>
                  <p className="text-emerald-100 mt-1">数智化运营系统</p>
                </div>
              </div>
            </div>

            {/* System Features */}
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">系统简介</h2>
                <p className="text-emerald-100 leading-relaxed">
                  基于现代化技术栈打造的中医调理中心管理系统，集成客户管理、预约调度、诊疗记录、场地管理等核心功能，为您的诊疗服务提供全方位数字化支持。
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                    <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium">客户管理</h3>
                    <p className="text-sm text-emerald-100">完整的客户档案管理</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                    <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a4 4 0 118 0v4m-4 8h0m0 0h0m0 0v3m0-3v0a3 3 0 003-3V8a1 1 0 00-1-1H9a1 1 0 00-1 1v1a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium">预约管理</h3>
                    <p className="text-sm text-emerald-100">智能预约调度系统</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                    <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium">诊疗记录</h3>
                    <p className="text-sm text-emerald-100">专业电子病历系统</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                    <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium">场地管理</h3>
                    <p className="text-sm text-emerald-100">诊室资源智能分配</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-12">
              <p className="text-sm text-emerald-100">
                © 2025 国保中医调理中心 & HyCore Tech
              </p>
            </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12 relative">
          {/* Background decoration for right side */}
          <div className="absolute inset-0 bg-gradient-to-bl from-white via-emerald-50/20 to-emerald-100/30"></div>
          <div className="absolute top-16 right-16 w-20 h-20 bg-emerald-100/30 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 left-16 w-16 h-16 bg-blue-100/40 rounded-full blur-lg"></div>
          
          <div className="w-full max-w-md relative z-10">
            <Card variant="glass" padding="large" className="backdrop-blur-xl border-white/30">
              {/* Mobile Header (only visible on small screens) */}
              <div className="lg:hidden text-center mb-8">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                </div>
                <h2 className="text-xl font-bold text-gray-900">国保中医调理中心</h2>
                <p className="text-sm text-gray-600">数智化运营系统</p>
              </div>

                             {/* Demo Account Info */}
               <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50/80 to-blue-50/80 border border-emerald-200/50 rounded-xl backdrop-blur-sm">
                 <div className="flex items-center">
                   <div className="flex-shrink-0">
                     <svg className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                   </div>
                   <div className="ml-3 flex-1">
                     <p className="text-sm font-medium text-emerald-800 mb-1">演示账户</p>
                     <div className="text-xs text-emerald-700 space-x-4">
                       <span>用户名: <code className="bg-white/60 px-1.5 py-0.5 rounded text-emerald-800 border border-emerald-200/50">admin</code></span>
                       <span>密码: <code className="bg-white/60 px-1.5 py-0.5 rounded text-emerald-800 border border-emerald-200/50">admin123</code></span>
                     </div>
                   </div>
                 </div>
               </div>

              <form className="space-y-6" onSubmit={handleSubmit}>
                {/* Error Alert */}
                {error && (
                  <Alert type="error">
                    {error}
                  </Alert>
                )}
                
                {/* Username Input */}
                <Input
                  label="用户名"
                  type="text"
                  placeholder="请输入用户名"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  startIcon={UserIcon}
                  required
                  disabled={loading}
                />

                {/* Password Input */}
                <Input
                  label="密码"
                  type="password"
                  placeholder="请输入密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  startIcon={LockIcon}
                  required
                  disabled={loading}
                />

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <Checkbox
                    label="记住我"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                  />

                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => alert('请联系系统管理员重置密码')}
                    type="button"
                  >
                    忘记密码?
                  </Button>
                </div>

                {/* Login Button */}
                <Button
                  type="submit"
                  size="large"
                  loading={loading}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? '登录中...' : '登录'}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;